import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import Pagination, { getPageSize } from '../../Pagination';
import Fab from '../../Fab';
import PeakToolbar, { type BarAction } from '../../PeakToolbar';
import DlBtn from '../../DlBtn';
import { currencyCode, fmtDateTime, money } from '../../../utils/region';
import {
  payrollApi, PR_DOC_TYPES, PR_FILE_MAX_BYTES, PR_PHOTO_TYPES, PR_TRI_MARKERS, PrAccount, PrCheck, PrCounts, PrDoc, PrEmployeeBody, PrFieldMeta, PrHistoryRow, PrImportCommit, PrIssue, PrListPage, PrSchema, PrStatus, PrTier,
  PrEmpRunsPage, PrYtdBody, PrYtdOpening,
} from '../../../utils/api/payroll';
import type { AwCtx } from '../ctx';
import { btn, btnPrimary, card, dmy, errBox, inp, note, num, openBlob, pillAmber, pillGreen, pillGrey, pillRed, sectionHead, table, td, th } from '../ui';
import { Dialog, Foot, Loader, actBtn, chip, chipOn, emptyLine, field, lbl } from '../dialog';
import { isReadOnlySeat } from './fixedAssets/shared';
import { TF, fieldLabel, fieldsList, historyValue, mmYyyy, monthOrDay, prErr, runStatusLabel, runTypeLabel, sourceLabel, ytdSourceLabel } from './payrollText';
import { EmployeeImportWindow } from './PayrollImport';

// ── Payroll › Employees (batch 10 R3 — PAY-2, PAY-3, PAY-4, PAY-5) ──────────────────────────────────
// The client company's employees as payroll needs them: a list with the three "missing" indicators the
// client asked for (run payroll · pay · file), and one full-screen window per employee — Details in six
// sections, Documents, History, Payroll — with a readiness panel that lists, tier by tier, what is still
// missing and jumps to the field.
//
// ⛔ The rules are the SERVER's (services/employeeFields.ts). This screen draws its stars from
// GET /employee-schema and asks POST /employees/check what is missing as the operator types; it never
// decides on its own whether a field is required (design §3.1). A new employee cannot be saved without
// the "on file" tier (the server answers 400 naming each field); everything else is shown, never blocking.
// A conditional field (Thai names, a foreigner's passport, a transfer's bank…) is starred only while the
// server reports it, so a Thai employee carries no passport star.
// ⛔ Save never sends what the server is certain to refuse — a refused non-GET pages the admins. It asks
// /employees/check about the draft FIRST and stops, the reason under each field and the cursor in the
// first, when a new employee lacks an "on file" field, when an edit adds an "on file" gap the record did
// not have when it was read (field:code, the server's own comparison), or when the server names any value
// in `formats` (a shape it refuses, or an employee code a colleague already holds). A chosen file is checked
// against the accepted types and the size limit the same way. The server's refusal is the backstop, never
// the plan.
// ⛔ Pay: the whole screen sits behind O2 on the server (/api/payroll → 403 pay_forbidden) and is not even
// offered to a client's own books login (menus.ts staffOnly). The bank account is masked until someone
// asks to see it; the list only ever has ••••last4.
// ⛔ The employee's status is derived from the dates by the server, never chosen here: a last day before
// today reads Resigned or Terminated, a probation still running reads Probation, anything else Active.
// ⛔ (R4, PAY-7) The list's toolbar is the house PeakToolbar — one row, never two: the filters scroll in their
// own box, Export stays (`right` never folds), and Import · Download template · Assign codes fold into More
// when the filters need the width. Export and the template are reads, offered to every seat; Import, Assign
// codes and the + are writes, left OUT for a read-only seat (books / auditor), never shown disabled. The words
// both this file and the import window say live in payrollText.ts (this file renders the window: exporting
// them from here would be an import cycle).
// ⛔ (R5, appendix D3) The welfare fund is ANSWERED, not ticked: a field the schema marks `tri` is a choice of
// blank · Yes · No, sent as null / 1 / 0. Whether it has been answered is the server's marker
// (welfare_answered), derived by the employee door from what is sent and never sent from here; the
// run-payroll tier asks for the answer only while the client has its welfare fund switched on (Payroll
// settings), and the panel says so from the server's answer like every other tier.
// ⛔ (R6) The Payroll tab: the runs this person was on (GET /employees/:id/runs, 100 a page), each a jump to its
// run in Payroll › Payroll List through ctx.setNav — the shell's guarded door, never a second guard here and
// never ?menu / ?sub written by hand — and the tax year as the payroll engine reads it, month by month, with the
// year-to-date opening that covers the months before payroll ran here (GET / PUT /employees/:id/ytd-openings).
// Every figure there is the server's; the window adds nothing up. The opening is a small form with `dirty`; a
// read-only seat gets no Edit. ?etab=payroll (a run's "Fix" for a gap) opens the window on this tab: read ONCE
// when the window opens, never written by a tab click, dropped with ?emp when the window closes.

type Form = Record<string, string>;
/** PATCH /employees/:id's `wageSync` (services/salarySync.ts WageSyncResult): what this month's ภ.ง.ด.1 / สปส. line
 *  did with a new salary. 'payroll' (batch 10 R7) = a posted run owns it, so it did not move. */
type WageSyncAnswer = { year: number; month: number; pnd1: string; sso: string };
const TIER_KEY: Record<PrTier, string> = { C: 'c', P: 'p', $: 'pay', F: 'f' };
const STATUS_FILTERS = ['current', 'active', 'probation', 'resigned', 'terminated', 'all'] as const;
const statusPill = (s: PrStatus | string): React.CSSProperties =>
  s === 'active' ? pillGreen : s === 'probation' ? pillAmber : s === 'terminated' ? pillRed : pillGrey;
const NUMERIC = new Set(['monthly_salary', 'daily_rate', 'probation_days']);
/** Why the route would refuse a chosen file — a type it does not take (400 file_type) or one over its size
 *  limit — or '' when it would take it. Asked before anything is sent. */
const fileProblem = (t: TF, f: File, types: readonly string[]): string =>
  !types.includes(f.type) ? t('prl_err_file_type') : f.size >= PR_FILE_MAX_BYTES ? t('prl_err_file_too_big', { mb: PR_FILE_MAX_BYTES / (1024 * 1024) }) : '';
/** A stored employee as the form's strings (every field the schema lists). A three-way field (`tri`, R5 —
 *  the welfare fund, appendix D3) reads '' until it has been answered — the server's marker beside it, or a
 *  tick made before R5 (a member has answered) — and then '1' / '0'. */
function toForm(schema: PrSchema, b: PrEmployeeBody | null): Form {
  const out: Form = {};
  for (const f of schema.fields) {
    const v = f.store === 'details' ? b?.details?.[f.key] : b?.employee?.[f.key];
    const answered = f.tri && (Number(b?.employee?.[PR_TRI_MARKERS[f.key] || '']) || Number(v));
    out[f.key] = f.kind === 'bool' ? (f.tri && !answered ? '' : Number(v) ? '1' : '0') : v === null || v === undefined ? '' : String(v);
  }
  return out;
}
/** The value a field is sent as: numbers as numbers (blank → null), a tick as 0 / 1, text trimmed. A
 *  three-way field sends null for its blank — "not answered" — and 0 / 1 for No / Yes.
 *  ⛔ Its "answered" marker is the SERVER's to derive from this (services/employeeWrite.ts welfareAnswer):
 *  the window never sends it — the route refuses a write that names it. And a new employee's untouched
 *  blank is not sent at all (the draft below leaves out every blank of a new record): the record starts
 *  unanswered, never "No". */
const sendValue = (f: PrFieldMeta, v: string): any => (NUMERIC.has(f.key) ? (v.trim() === '' ? (f.key === 'monthly_salary' ? 0 : null) : Number(v))
  : f.kind === 'bool' ? (f.tri && v === '' ? null : v === '1' ? 1 : 0) : v.trim());
const ymd = (iso: string) => (iso ? monthOrDay(iso) : '—');
/** The tax allowances the Tax module keeps for this person (client_employees.pnd1_allowances, the list
 *  routes/pnd1.ts writes): read here, never edited here. */
function allowancesOf(raw: any): { count: number; total: number } {
  try {
    const list = JSON.parse(String(raw || '[]'));
    const items = Array.isArray(list) ? list.filter((x: any) => x && String(x.name || '').trim()) : [];
    return { count: items.length, total: items.reduce((n: number, x: any) => n + (Number(x.amount) || 0), 0) };
  } catch { return { count: 0, total: 0 }; }
}

// ── The list ────────────────────────────────────────────────────────────────────────────────────────
export function EmployeesPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [schema, setSchema] = useState<PrSchema | null>(null);
  const [status, setStatus] = useState<string>('current');
  const [dept, setDept] = useState('');
  const [missing, setMissing] = useState(false);
  const [q, setQ] = useState('');
  const [qd, setQd] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [data, setData] = useState<PrListPage | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [tplBusy, setTplBusy] = useState(false);
  const [notice, setNotice] = useState('');
  // ⛔ A read-only seat gets no write control at all (never one shown disabled): every non-GET it sent would be
  // refused, and a refused write pages the admins.
  const readOnly = isReadOnlySeat(ctx);
  const openId = params.get('emp') || '';
  const openTab = params.get('etab') || '';
  useEffect(() => { const h = setTimeout(() => { setQd(q); setPage(1); }, 220); return () => clearTimeout(h); }, [q]);
  useEffect(() => {
    let alive = true;
    payrollApi.schema(ctx.clientId).then(s => { if (alive) setSchema(s); }).catch((e: any) => { if (alive) setErr(prErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let alive = true;
    payrollApi.employees(ctx.clientId, { status, department: dept, missing, q: qd, page, limit })
      .then(d => { if (alive) { setData(d); setErr(''); } })
      .catch((e: any) => { if (alive) { setData(null); setErr(prErr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, reload, status, dept, missing, qd, page, limit]);   // eslint-disable-line react-hooks/exhaustive-deps
  // ?emp=<id> opens that employee's window (the deep link colleagues hand each other); closing drops it. (R6) ?etab=
  // names the tab it opens on — only a link sets it, so opening a row from the list and closing the window both
  // drop it with the same write.
  const setOpen = (id: string) => setParams(p => {
    const n = new URLSearchParams(p);
    if (id) n.set('emp', id); else n.delete('emp');
    n.delete('etab');
    return n;
  }, { replace: true });
  const refresh = () => setReload(n => n + 1);
  const assign = async () => {
    if (!data?.missingCodes || busy) return;
    if (!window.confirm(t('prl_assign_confirm', { count: data.missingCodes }))) return;
    setBusy(true); setErr('');
    try { await payrollApi.assignCodes(ctx.clientId); refresh(); } catch (e: any) { setErr(prErr(e, t)); } finally { setBusy(false); }
  };
  // The template is a download like Export, so it spins while it runs — and, folded into More, the More
  // button spins for it (PeakToolbar): the house rule that every download shows it is working.
  const template = () => {
    if (tplBusy) return;
    setTplBusy(true); setErr('');
    payrollApi.template(ctx.clientId).catch((e: any) => setErr(prErr(e, t))).finally(() => setTplBusy(false));
  };
  const imported = (r: PrImportCommit) => {
    setImporting(false);
    setNotice(t('prl_imp_done', { created: r.created, updated: r.updated }));
    refresh(); ctx.bump();
  };
  const counts = data?.counts;
  const rows = data?.rows || [];
  const filters = (
    <>
      <select value={dept} onChange={e => { setDept(e.target.value); setPage(1); }} style={{ ...inp, flex: 'none' }} aria-label={t('prl_f_department')} data-filter="department">
        <option value="">{t('prl_all_departments')}</option>
        {(data?.departments || []).map(d => <option key={d} value={d}>{d}</option>)}
      </select>
      <button onClick={() => { setMissing(m => !m); setPage(1); }} aria-pressed={missing} data-filter="missing"
        style={{ ...btn, flex: 'none', whiteSpace: 'nowrap', ...(missing ? { background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' } : {}) }}>
        <i className="ti ti-alert-circle" aria-hidden /> {t('prl_missing_only')}
      </button>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('prl_search_ph')} style={{ ...inp, flex: 'none', width: 300 }} data-filter="search" />
      <span style={{ fontSize: 12, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }} data-count>{data ? t('prl_n_employees', { count: data.total }) : ''}</span>
    </>
  );
  // ⛔ The sheet answers the question the list is showing: the same status, department, "missing" and search
  // (the debounced one the list itself sent), every page of it. It sits in `right`, which never folds.
  const right = (
    <DlBtn style={btn} title={t('prl_exp_title')} onRun={() => payrollApi.exportEmployees(ctx.clientId, { status, department: dept, missing, q: qd })}>
      <i className="ti ti-file-spreadsheet" aria-hidden /> {t('prl_exp_btn')}
    </DlBtn>
  );
  const actions: BarAction[] = [
    ...(readOnly ? [] : [{ key: 'import', label: t('prl_imp_btn'), icon: 'ti-file-import', title: t('prl_imp_btn_title'), onClick: () => setImporting(true) }]),
    { key: 'template', label: t('prl_tpl_btn'), icon: 'ti-download', title: t('prl_tpl_title'), busy: tplBusy, onClick: template },
    ...(readOnly || !data?.missingCodes ? [] : [{ key: 'assign', label: t('prl_assign_codes', { count: data.missingCodes }), icon: 'ti-hash', busy, onClick: assign }]),
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-pane="payroll-employees">
      {/* status chips — Current (Active + Probation) by default, one click to All */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {STATUS_FILTERS.map(s => (
          <button key={s} data-status-chip={s} onClick={() => { setStatus(s); setPage(1); }} aria-pressed={s === status}
            style={{ ...chip, ...(s === status ? chipOn : {}) }}>
            {t(`prl_status_${s}`)}{counts ? ` · ${counts[s] ?? 0}` : ''}
          </button>
        ))}
      </div>
      {notice && (
        <div style={{ ...card, padding: '9px 12px', fontSize: 12.5, color: 'var(--text-secondary)', display: 'flex', gap: 8, alignItems: 'flex-start' }} data-imp-notice>
          <i className="ti ti-circle-check" style={{ flexShrink: 0, marginTop: 1, color: '#166534' }} aria-hidden />
          <div style={{ flex: 1 }}>{notice}</div>
          <button type="button" onClick={() => setNotice('')} aria-label={t('aw_dlg_close')} style={{ ...actBtn, padding: '2px 6px' }}><i className="ti ti-x" aria-hidden /></button>
        </div>
      )}
      {err && <div style={errBox}>{err}</div>}
      <div style={{ ...card, padding: 0, overflow: 'visible' }}>
        {/* department · missing info · search · the count, then Export; the rest fold into More (never a second row) */}
        <PeakToolbar filters={filters} right={right} actions={actions} />
        <div style={{ overflowX: 'auto' }}>
          {data === null ? (err ? null : <Loader />) : !rows.length ? <div style={emptyLine}>{t('prl_none')}</div> : (
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>{t('prl_col_code')}</th><th style={th}>{t('prl_col_name')}</th><th style={th}>{t('prl_f_department')}</th>
                  <th style={th}>{t('prl_f_position')}</th><th style={th}>{t('prl_f_email')}</th><th style={th}>{t('prl_f_mobile')}</th>
                  <th style={th}>{t('prl_col_start')}</th><th style={th}>{t('prl_col_status')}</th><th style={{ ...th, textAlign: 'right' }}>{t('prl_col_pay')}</th>
                  <th style={th}>{t('prl_col_missing')}</th><th style={th}>{t('prl_col_bank')}</th><th style={th}>{t('prl_col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} data-emp-row={r.id}>
                    <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{r.emp_code || <span style={{ color: 'var(--text-tertiary)' }}>—</span>}</td>
                    <td style={td}>
                      <button onClick={() => setOpen(r.id)} data-open-emp={r.id}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 700, color: 'var(--accent)', fontSize: 12.5, textAlign: 'left' }}>{r.name || '—'}</button>
                      {(r.nickname || (r.name_en && !r.name.includes(r.name_en))) && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{[r.name_en && !r.name.includes(r.name_en) ? r.name_en : '', r.nickname].filter(Boolean).join(' · ')}</div>}
                    </td>
                    <td style={td}>{r.department || '—'}</td>
                    <td style={td}>{r.position || '—'}</td>
                    <td style={td}>{r.email || '—'}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.mobile || '—'}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{ymd(r.start_date)}</td>
                    <td style={td}>
                      <span style={statusPill(r.status)} data-status={r.status}>{t(`prl_status_${r.status}`)}</span>
                      {r.leaving_on && <div style={{ fontSize: 11, color: '#92400E', marginTop: 3 }}>{t('prl_leaving_on', { date: dmy(r.leaving_on) })}</div>}
                      {r.status === 'probation' && r.probation_end && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 3 }}>{t('prl_probation_until', { date: dmy(r.probation_end) })}</div>}
                    </td>
                    <td style={num}>{r.emp_type === 'daily' ? (r.daily_rate == null ? '—' : `${money(r.daily_rate)} ${t('prl_per_day')}`) : money(r.monthly_salary)}
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t(r.emp_type === 'daily' ? 'prl_type_daily' : 'prl_type_monthly')}</div></td>
                    <td style={td}><MissingChips m={r.missing} checksum={r.checksum} t={t} /></td>
                    <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }} data-bank={r.id}>{r.bank_account || '—'}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}><button onClick={() => setOpen(r.id)} style={actBtn} data-row-open={r.id}><i className="ti ti-pencil" aria-hidden /> {t('prl_open')}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {data && <Pagination page={page} total={data.total} limit={data.limit || limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />}
      <div style={note}>{t('prl_list_note')}</div>
      {!readOnly && <Fab onClick={() => setCreating(true)} title={t('prl_new_title')} />}
      {schema && importing && !readOnly && <EmployeeImportWindow ctx={ctx} schema={schema} onClose={() => setImporting(false)} onDone={imported} />}
      {schema && creating && <EmployeeWindow ctx={ctx} schema={schema} id="" onClose={() => setCreating(false)} onSaved={() => { setCreating(false); refresh(); }} />}
      {schema && openId && !creating && <EmployeeWindow key={openId} ctx={ctx} schema={schema} id={openId} initialTab={openTab} onClose={() => setOpen('')} onSaved={refresh} />}
    </div>
  );
}

/** The client's three indicators — run payroll · pay · file — and the record's own gaps, when there are any. */
function MissingChips({ m, checksum, t }: { m: PrCounts; checksum: boolean; t: TF }) {
  const items: [PrTier, number][] = [['C', m.C], ['P', m.P], ['$', m.$], ['F', m.F]];
  const shown = items.filter(([, n]) => n > 0);
  if (!shown.length && !checksum) return <span style={{ ...pillGreen }} data-missing="none"><i className="ti ti-check" aria-hidden /> {t('prl_ready')}</span>;
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {shown.map(([tier, n]) => (
        <span key={tier} style={tier === 'C' ? pillGrey : pillAmber} data-missing={TIER_KEY[tier]} title={t(`prl_tier_${TIER_KEY[tier]}_hint`)}>
          {t(`prl_tier_${TIER_KEY[tier]}`)} · {n}
        </span>
      ))}
      {checksum && <span style={pillAmber} data-missing="checksum" title={t('prl_issue_checksum')}><i className="ti ti-alert-triangle" aria-hidden /></span>}
    </div>
  );
}

// ── The readiness panel: what is missing, tier by tier; a click jumps to the field ───────────────────
// ⛔ "Nothing missing" is said only on the server's answer: until the first one arrives the panel says it
// is checking, and when /check fails it says so — an empty list is not a clean bill.
type Answer = 'checking' | 'error' | 'ready';
function Readiness({ issues, answer, onJump, t }: { issues: PrIssue[]; answer: Answer; onJump: (field: string) => void; t: TF }) {
  return (
    <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 10 }} data-readiness data-readiness-state={answer}>
      <div style={sectionHead}>{t('prl_readiness')}</div>
      {answer === 'checking' && <div style={{ fontSize: 12, color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 6 }}><i className="ti ti-loader" aria-hidden /> {t('prl_readiness_checking')}</div>}
      {answer === 'error' && <div style={{ fontSize: 12, color: '#B42318' }} data-readiness-error>{t('prl_readiness_error')}</div>}
      {answer === 'ready' && (['C', 'P', '$', 'F'] as PrTier[]).map(tier => {
        const mine = issues.filter(i => i.tier === tier);
        const errors = mine.filter(i => i.severity === 'error');
        return (
          <div key={tier} data-tier={TIER_KEY[tier]}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)' }}>
              <i className={errors.length ? 'ti ti-circle-dashed' : 'ti ti-circle-check'} style={{ color: errors.length ? '#B42318' : '#166534' }} aria-hidden />
              {t(`prl_tier_${TIER_KEY[tier]}`)}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', margin: '2px 0 4px 20px' }}>{t(`prl_tier_${TIER_KEY[tier]}_hint`)}</div>
            {!mine.length ? <div style={{ fontSize: 11.5, color: '#166534', marginLeft: 20 }}>{t('prl_tier_complete')}</div> : (
              <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {mine.map(i => (
                  <li key={`${i.field}:${i.code}`} style={{ fontSize: 12, listStyle: 'none' }}>
                    <button onClick={() => onJump(i.field)} data-jump={i.field}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', color: i.severity === 'warn' ? '#92400E' : '#B42318', fontSize: 12 }}>
                      {fieldLabel(t, i.field)} — {t(`prl_issue_${i.code}`)}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── The employee window ────────────────────────────────────────────────────────────────────────────
type Tab = 'details' | 'documents' | 'history' | 'payroll';
const TABS: readonly Tab[] = ['details', 'documents', 'history', 'payroll'];
function EmployeeWindow({ ctx, schema, id, initialTab = '', onClose, onSaved }: {
  ctx: AwCtx; schema: PrSchema; id: string; initialTab?: string; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isNew = !id;
  const [body, setBody] = useState<PrEmployeeBody | null>(null);
  const [orig, setOrig] = useState<Form>(() => toForm(schema, null));
  const [form, setForm] = useState<Form>(() => toForm(schema, null));
  const [issues, setIssues] = useState<PrIssue[]>([]);
  // ?etab= is read here, once, as the window opens (a new employee always starts on Details).
  const [tab, setTab] = useState<Tab>(() => (id && (TABS as readonly string[]).includes(initialTab) ? initialTab as Tab : 'details'));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [stale, setStale] = useState(false);
  // Batch 10 R7 (R7-19): a save whose salary did NOT reach this month's statutory lines because a posted payroll
  // run owns them (salarySync answers 'payroll'). Said once, after that save; any other answer says nothing.
  const [paySync, setPaySync] = useState(false);
  const [showAcct, setShowAcct] = useState(false);
  const [accounts, setAccounts] = useState<PrAccount[]>([]);
  const [split, setSplit] = useState<Record<string, string> | null>(null);
  const [separating, setSeparating] = useState(false);
  // FS-12, the house precedent (Admin/JobRequiredDocs.tsx, Expenses ClaimDialog): the EDIT PATH sets it and
  // nothing else does — never a diff against the values the window opened with.
  const [dirty, setDirty] = useState(false);
  // A file chosen or a description typed on the Documents tab and not uploaded yet. It reaches the shell's
  // close question through the Dialog's `dirty` alone (never a question of its own) and holds nothing else
  // up: Save and the one-click actions read the form's own flag. While something waits there the tab stays
  // mounted, only hidden, when another tab is shown: a tab switch neither drops it nor the question.
  const [docsPending, setDocsPending] = useState(false);
  // The server's last answer about the draft (POST /employees/check): values in a shape it refuses, the
  // panel's state, and the probation end it worked out (null = it gave none; the record's is shown).
  const [formats, setFormats] = useState<Record<string, string>>({});
  const [answer, setAnswer] = useState<Answer>('checking');
  const [checkEnd, setCheckEnd] = useState<string | null>(null);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  const setF = (k: string) => (v: string) => setForm(f => ({ ...f, [k]: v }));
  const take = (b: PrEmployeeBody) => {
    const f = toForm(schema, b); setBody(b); setOrig(f); setForm(f);
    setIssues(b.issues || []); setFormats({}); setCheckEnd(null); setAnswer('ready');
  };
  const heard = (r: PrCheck) => {
    setIssues(r.issues || []); setFormats(r.formats || {});
    setCheckEnd(typeof r.probationEnd === 'string' ? r.probationEnd : null); setAnswer('ready');
  };
  useEffect(() => {
    let alive = true;
    if (id) payrollApi.employee(ctx.clientId, id).then(b => { if (alive) take(b); }).catch((e: any) => { if (alive) setErr(prErr(e, t)); });
    payrollApi.expenseAccounts(ctx.clientId).then(a => { if (alive) setAccounts(a.rows || []); }).catch(() => { if (alive) setAccounts([]); });
    return () => { alive = false; };
  }, [ctx.clientId, id]);   // eslint-disable-line react-hooks/exhaustive-deps
  // What is sent: for a new employee every filled field; for an existing one only what changed.
  const draft = useMemo(() => {
    const out: Record<string, any> = {};
    for (const f of schema.fields) {
      const v = form[f.key] ?? '';
      if (isNew ? v.trim() !== '' : v !== (orig[f.key] ?? '')) out[f.key] = sendValue(f, v);
    }
    return out;
  }, [form, orig, schema, isNew]);
  // The readiness panel follows the typing — the SERVER's answer (POST /employees/check), a moment after the last key.
  const draftKey = JSON.stringify(draft);
  useEffect(() => {
    if (!isNew && !body) return;
    let alive = true;
    const h = setTimeout(() => {
      payrollApi.check(ctx.clientId, { ...(isNew ? {} : { id }), ...draft }).then(r => { if (alive) heard(r); }).catch(() => { if (alive) setAnswer('error'); });
    }, 300);
    return () => { alive = false; clearTimeout(h); };
  }, [draftKey, body, isNew, id, ctx.clientId]);   // eslint-disable-line react-hooks/exhaustive-deps
  const issueOf = (k: string) => issues.find(i => i.field === k && i.severity === 'error');
  /** What is wrong with a field, in the server's words: a refused shape first (that is what a save meets
   *  first), else what a tier misses. */
  const problemOf = (k: string): string => formats[k] || issueOf(k)?.code || '';
  const jump = (fieldKey: string) => {
    setTab('details');
    const target = fieldKey === 'name' ? 'first_name_th' : fieldKey;
    setTimeout(() => { const el = document.getElementById(`prl-f-${target}`); if (el) { el.scrollIntoView?.({ block: 'center' }); (el as HTMLInputElement).focus(); } }, 30);
  };
  /** What in the server's answer would get this save refused — the route's own three tests, in its order:
   *  a value in a refused shape (400 invalid), then the "on file" tier — all of it for a new employee (400
   *  tier_c), only a gap the stored record did not have for an edit (400 tier_c_worse, keyed field:code). */
  const refusalOf = (r: PrCheck): { fields: string[]; message: string } => {
    const had = new Set((body?.issues || []).filter(i => i.tier === 'C' && i.severity === 'error').map(i => `${i.field}:${i.code}`));
    const gaps = Array.from(new Set((r.issues || []).filter(i => i.tier === 'C' && i.severity === 'error' && (isNew || !had.has(`${i.field}:${i.code}`))).map(i => i.field)));
    const shapes = Object.keys(r.formats || {});
    const order = schema.fields.map(f => f.key);
    const named = [...shapes, ...gaps];
    const fields = [...order.filter(k => named.includes(k)), ...named.filter(k => !order.includes(k))];
    const message = [
      shapes.length ? t('prl_err_invalid', { fields: fieldsList(t, shapes) }) : '',
      !gaps.length ? '' : isNew ? t('prl_err_tier_c', { fields: fieldsList(t, gaps) }) : t('prl_err_tier_c_worse', { fields: fieldsList(t, gaps) }),
    ].filter(Boolean).join(' ');
    return { fields, message };
  };
  const save = async () => {
    if (busy || (!isNew && !body)) return;
    setBusy(true); setErr(''); setStale(false); setPaySync(false);
    try {
      // ⛔ Ask first, send second (the rule at the top): the answer also redraws the panel and the fields.
      let r: PrCheck;
      try { r = await payrollApi.check(ctx.clientId, { ...(isNew ? {} : { id }), ...draft }); }
      catch (e: any) { setAnswer('error'); setErr(prErr(e, t)); return; }
      heard(r);
      const no = refusalOf(r);
      if (no.fields.length) { setErr(no.message); jump(no.fields[0]); return; }
      if (isNew) { await payrollApi.create(ctx.clientId, draft); setDirty(false); onSaved(); return; }
      const b = await payrollApi.update(ctx.clientId, id, body!.rowVersion, draft);
      take(b); setDirty(false); onSaved();
      const ws = (b as { wageSync?: WageSyncAnswer | null }).wageSync;
      setPaySync(ws?.pnd1 === 'payroll' || ws?.sso === 'payroll');
    } catch (e: any) {
      setErr(prErr(e, t));
      if (e?.payload?.code === 'stale') setStale(true);
    } finally { setBusy(false); }
  };
  const reloadNow = async () => {
    setBusy(true); setErr(''); setStale(false);
    try { take(await payrollApi.employee(ctx.clientId, id)); setDirty(false); } catch (e: any) { setErr(prErr(e, t)); } finally { setBusy(false); }
  };
  // One-click actions on a saved record write at once (and are logged); with typing pending they wait for Save.
  // Each answers whether it went through. A refusal is said through `say` — the window's error line, or the
  // small dialog that asked for it, which then stays open with what was typed; a stale record is also said on
  // the window's line, where its Reload is.
  const act = async (fn: () => Promise<PrEmployeeBody>, say: (m: string) => void = setErr): Promise<boolean> => {
    if (busy || !body) return false;
    if (dirty) { say(t('prl_save_first')); return false; }
    setBusy(true); setErr('');
    try { take(await fn()); onSaved(); return true; } catch (e: any) {
      const m = prErr(e, t);
      say(m);
      if (e?.payload?.code === 'stale') { setStale(true); setErr(m); }
      return false;
    } finally { setBusy(false); }
  };
  const separate = async (v: { date: string; kind: string; reason: string }): Promise<string> => {
    let why = '';
    if (await act(() => payrollApi.separate(ctx.clientId, id, body!.rowVersion, v), m => { why = m; })) setSeparating(false);
    return why;
  };
  const useSsoDate = () => act(() => payrollApi.useSsoDate(ctx.clientId, id, body!.rowVersion));
  const reinstate = () => { if (window.confirm(t('prl_reinstate_confirm'))) act(() => payrollApi.reinstate(ctx.clientId, id, body!.rowVersion)); };
  const remove = async () => {
    if (busy || dirty || !body || !window.confirm(t('prl_delete_confirm'))) return;
    setBusy(true); setErr('');
    try { await payrollApi.remove(ctx.clientId, id, body.rowVersion); onSaved(); onClose(); } catch (e: any) { setErr(prErr(e, t)); setBusy(false); }
  };
  const askSplit = async () => {
    try { setSplit((await payrollApi.splitName(ctx.clientId, id)).suggestion || {}); } catch (e: any) { setErr(prErr(e, t)); }
  };
  const applySplit = () => { if (split) { ed(setForm, { ...form, ...Object.fromEntries(Object.entries(split).filter(([, v]) => v)) }); setSplit(null); } };

  const emp = body?.employee || {};
  const titleName = isNew ? t('prl_new_title') : String(emp.name || '') || t('prl_unnamed');
  const fieldsOf = (section: string) => schema.fields.filter(f => f.section === section);
  // The "on file" star: always on a field the tier always needs; on a conditional one only while the server
  // reports it for this employee (no local rule for who is Thai or foreign).
  const star = (f: PrFieldMeta) => {
    if (!f.tiers.includes('C')) return null;
    if (f.conditional && !issues.some(i => i.tier === 'C' && i.field === f.key)) return null;
    return <span aria-hidden style={{ color: issueOf(f.key) ? '#B42318' : 'var(--text-tertiary)', marginLeft: 3 }} data-star={f.key}>*</span>;
  };
  const box = (f: PrFieldMeta): React.CSSProperties => ({ ...field, ...(problemOf(f.key) ? { border: '1px solid #F3B9B9', background: '#FFF8F8' } : {}) });
  const optLabel = (f: PrFieldMeta, o: string) => t(`prl_opt_${f.key}_${o || 'blank'}`);
  const fieldEl = (f: PrFieldMeta) => {
    const v = form[f.key] ?? '';
    const idAttr = `prl-f-${f.key}`;
    let control: React.ReactNode;
    if (f.key === 'bank_account' && !showAcct && !isNew && orig.bank_account) {
      control = (
        <div style={{ display: 'flex', gap: 6 }}>
          <input id={idAttr} value={`••••${String(body?.details?.bank_account_last4 || '')}`} readOnly style={{ ...box(f), color: 'var(--text-secondary)' }} data-masked-account />
          <button type="button" onClick={() => setShowAcct(true)} style={actBtn} data-action="show-account"><i className="ti ti-eye" aria-hidden /> {t('prl_show_account')}</button>
        </div>
      );
    } else if (f.kind === 'select') {
      control = (
        <select id={idAttr} value={v} onChange={e => ed(setF(f.key), e.target.value)} style={box(f)}>
          {!(f.options || []).includes('') && <option value="">{t('prl_choose')}</option>}
          {(f.options || []).map(o => <option key={o || 'blank'} value={o}>{optLabel(f, o)}</option>)}
        </select>
      );
    } else if (f.kind === 'account') {
      control = (
        <select id={idAttr} value={v} onChange={e => ed(setF(f.key), e.target.value)} style={box(f)}>
          <option value="">{t('prl_account_default')}</option>
          {accounts.map(a => <option key={a.id} value={a.id}>{a.code} · {a.name_th || a.name_en}</option>)}
          {v && !accounts.some(a => a.id === v) && <option value={v}>{body?.expenseAccount ? `${body.expenseAccount.code} · ${body.expenseAccount.name_th || body.expenseAccount.name_en}` : v}</option>}
        </select>
      );
    } else if (f.kind === 'bool' && f.tri) {
      // (R5, D3) Not answered · Yes · No — a choice, never a tick: an unticked box cannot tell "No" from
      // "nobody asked", and the run-payroll tier asks for the answer while the client has a welfare fund.
      control = (
        <select id={idAttr} value={v} onChange={e => ed(setF(f.key), e.target.value)} style={box(f)} data-tri={f.key}>
          <option value="">{t('prl_blank')}</option>
          <option value="1">{t('prl_yes')}</option>
          <option value="0">{t('prl_no')}</option>
        </select>
      );
    } else if (f.kind === 'bool') {
      control = <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}><input id={idAttr} type="checkbox" checked={v === '1'} onChange={e => ed(setF(f.key), e.target.checked ? '1' : '0')} /> {t('prl_yes')}</label>;
    } else if (f.kind === 'textarea') {
      control = <textarea id={idAttr} value={v} onChange={e => ed(setF(f.key), e.target.value)} rows={2} style={{ ...box(f), resize: 'vertical' }} />;
    } else {
      const type = f.kind === 'date' ? 'date' : f.kind === 'money' || f.kind === 'int' ? 'number' : f.kind === 'email' ? 'email' : 'text';
      const ph = f.key === 'emp_code' && isNew ? t('prl_code_auto') : f.key === 'sso_status' ? t('prl_sso_status_ph') : '';
      control = <input id={idAttr} type={type} value={v} onChange={e => ed(setF(f.key), e.target.value)} placeholder={ph} list={f.key === 'title_th' ? 'prl-titles-th' : f.key === 'title_en' ? 'prl-titles-en' : undefined} style={box(f)} min={type === 'number' ? 0 : undefined} />;
    }
    return (
      <div key={f.key} data-field={f.key}>
        <label htmlFor={idAttr} style={lbl}>{fieldLabel(t, f.key)}{star(f)}</label>
        {control}
        {f.key === 'welfare_member' && f.tri && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 3 }} data-hint={f.key}>{t('prl_welfare_hint')}</div>}
        {problemOf(f.key) && <div style={{ fontSize: 11, color: '#B42318', marginTop: 3 }} data-issue={f.key}>{t(`prl_issue_${problemOf(f.key)}`)}</div>}
      </div>
    );
  };
  // Probation ends (read-only, derived, never stored — design §3.1): the server's date for what is typed
  // (/check's probationEnd), or the one the record was read with until /check gives one.
  const probEnd = checkEnd ?? String(body?.probationEnd || '');
  const probationEndEl = () => (
    <div key="probation_end" data-field="probation_end">
      <label htmlFor="prl-f-probation_end" style={lbl}>{t('prl_probation_ends')}</label>
      <input id="prl-f-probation_end" value={ymd(probEnd)} readOnly tabIndex={-1} style={{ ...field, color: 'var(--text-secondary)', background: 'var(--bg-app)' }} data-probation-end />
    </div>
  );
  const section = (key: string, extra?: React.ReactNode) => (
    <div key={key} style={{ ...card, display: 'flex', flexDirection: 'column', gap: 10 }} data-section={key}>
      <div style={sectionHead}>{t(`prl_sec_${key}`)}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 10 }}>
        {fieldsOf(key).flatMap(f => (f.key === 'probation_days' ? [fieldEl(f), probationEndEl()] : [fieldEl(f)]))}
      </div>
      {extra}
    </div>
  );
  const allow = allowancesOf(emp.pnd1_allowances);
  const taxWhere = `${t('aw_menu_tax')} › ภ.ง.ด.1`;   // the workspace's own menu words and the form's name, not translated
  const ssoDateOffer = !isNew && !!body && !String(emp.hire_date || '') && /^\d{4}-\d{2}-\d{2}$/.test(String(emp.sso_status || ''));
  const noStruct = !isNew && !!body && !['title_th', 'first_name_th', 'last_name_th', 'first_name_en', 'last_name_en'].some(k => String(emp[k] || '')) && !!String(emp.name || '');
  return (
    <Dialog title={titleName} docNo={String(emp.emp_code || '') || undefined} onClose={onClose} busy={busy} dirty={dirty || docsPending} fullscreen
      footer={<Foot onClose={onClose} onSubmit={save} busy={busy} ready={isNew || dirty} icon={isNew ? 'ti ti-user-plus' : 'ti ti-device-floppy'} label={isNew ? t('prl_create_btn') : t('prl_save_btn')} t={t} />}>
      <datalist id="prl-titles-th">{schema.titlesTh.map(x => <option key={x} value={x} />)}</datalist>
      <datalist id="prl-titles-en">{['Mr.', 'Mrs.', 'Ms.', 'Miss'].map(x => <option key={x} value={x} />)}</datalist>
      {!isNew && !body ? (err ? <div style={errBox}>{err}</div> : <Loader />) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 280px', gap: 14, alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
            {!isNew && body && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={statusPill(body.status)} data-window-status={body.status}>{t(`prl_status_${body.status}`)}</span>
                {body.leavingOn && <span style={pillAmber}>{t('prl_leaving_on', { date: dmy(body.leavingOn) })}</span>}
                {body.status === 'probation' && body.probationEnd && <span style={pillGrey} data-probation-pill>{t('prl_probation_until', { date: dmy(body.probationEnd) })}</span>}
                <span style={body.inSso ? pillGreen : pillGrey}>{t(body.inSso ? 'prl_in_sso' : 'prl_not_in_sso')}</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: 6 }} role="tablist">
              {TABS.map(k => (
                <button key={k} role="tab" aria-selected={tab === k} data-tab={k} disabled={isNew && k !== 'details'} onClick={() => setTab(k)}
                  style={{ ...chip, ...(tab === k ? chipOn : {}), opacity: isNew && k !== 'details' ? 0.5 : 1 }}>{t(`prl_tab_${k}`)}</button>
              ))}
            </div>
            {err && <div style={errBox} data-window-error>{err}{stale && <> <button onClick={reloadNow} style={{ ...actBtn, marginLeft: 8 }} data-action="reload">{t('prl_reload')}</button></>}</div>}
            {paySync && !err && (
              <div style={{ ...card, padding: '9px 12px', fontSize: 12.5, color: 'var(--text-secondary)', display: 'flex', gap: 8, alignItems: 'flex-start' }} data-wage-sync="payroll">
                <i className="ti ti-info-circle" style={{ flexShrink: 0, marginTop: 1, color: '#0E7C66' }} aria-hidden />
                <div style={{ flex: 1 }}>{t('prl_wage_sync_payroll', { pnd1: 'ภ.ง.ด.1', sso: 'สปส.' })}</div>
              </div>
            )}
            {tab === 'details' && (
              <>
                {noStruct && !split && (
                  <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg-app)' }}>
                    <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', flex: 1 }}>{t('prl_split_lead', { name: String(emp.name || '') })}</div>
                    <button onClick={askSplit} style={btn} data-action="split-name"><i className="ti ti-arrows-split" aria-hidden /> {t('prl_split_btn')}</button>
                  </div>
                )}
                {split && (
                  <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 8 }} data-split-preview>
                    <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{t('prl_split_preview')}</div>
                    <div style={{ fontSize: 12.5, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 6 }}>
                      {Object.entries(split).map(([k, v]) => <div key={k}><span style={{ color: 'var(--text-tertiary)' }}>{fieldLabel(t, k)}:</span> <b>{v || '—'}</b></div>)}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={applySplit} style={btnPrimary} data-action="split-apply">{t('prl_split_apply')}</button>
                      <button onClick={() => setSplit(null)} style={btn}>{t('prl_split_dismiss')}</button>
                    </div>
                  </div>
                )}
                {section('personal')}
                {section('employment', (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {/* The photo sits with the employment details, where the client's form has it (PAY-3, 2.2). */}
                    {!isNew && body && <PhotoBox ctx={ctx} body={body} disabled={busy || dirty} onDone={b => { take(b); onSaved(); }} t={t} />}
                    {ssoDateOffer && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }} data-sso-offer>
                        <i className="ti ti-calendar-question" style={{ color: '#92400E' }} aria-hidden />
                        <span>{t('prl_use_sso_date_q', { date: monthOrDay(String(emp.sso_status)) })}</span>
                        <button onClick={useSsoDate} disabled={busy || dirty} title={dirty ? t('prl_save_first') : ''} style={actBtn} data-action="use-sso-date">{t('prl_use_sso_date_btn')}</button>
                      </div>
                    )}
                    {!isNew && body && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5 }}>
                        {String(emp.termination_date || '')
                          ? <span data-separation>{t('prl_left_on', { date: dmy(String(emp.termination_date)) })}{emp.separation_kind ? ` · ${t(`prl_opt_separation_kind_${emp.separation_kind}`)}` : ''}{emp.separation_reason ? ` · ${String(emp.separation_reason)}` : ''}</span>
                          : <span style={{ color: 'var(--text-tertiary)' }}>{t('prl_employed')}</span>}
                        <div style={{ flex: 1 }} />
                        {String(emp.termination_date || '')
                          ? <button onClick={reinstate} disabled={busy || dirty} style={actBtn} data-action="reinstate"><i className="ti ti-user-check" aria-hidden /> {t('prl_reinstate_btn')}</button>
                          : <button onClick={() => setSeparating(true)} disabled={busy || dirty} style={actBtn} data-action="separate"><i className="ti ti-user-minus" aria-hidden /> {t('prl_separate_btn')}</button>}
                        {body.canDelete && <button onClick={remove} disabled={busy || dirty} style={{ ...actBtn, color: '#B42318' }} data-action="delete"><i className="ti ti-trash" aria-hidden /> {t('prl_delete_btn')}</button>}
                      </div>
                    )}
                  </div>
                ))}
                {section('contact')}
                {section('salary', !isNew && body ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {/* The client's 2.4 "Tax allowances / deductions": the Tax module's list, read here, edited there. */}
                    <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'baseline' }} data-allowances>
                      <b style={{ color: 'var(--text-primary)' }}>{t('prl_allowances_label')}</b>
                      <span>{allow.count ? t('prl_allowances_sum', { count: allow.count, total: money(allow.total) }) : t('prl_allowances_none')}</span>
                      <span style={{ color: 'var(--text-tertiary)' }}>· {t('prl_allowances_where', { where: taxWhere })}</span>
                    </div>
                    {(emp.wht_override != null || emp.sso_override != null) && (
                      <div style={note}>{t('prl_overrides_note', { wht: emp.wht_override != null ? money(emp.wht_override) : '—', sso: emp.sso_override != null ? money(emp.sso_override, 0) : '—' })}</div>
                    )}
                  </div>
                ) : null)}
                {section('payment')}
                {section('additional')}
                {!isNew && body && <div style={note}>{t('prl_audit_line', { created: String(emp.created_by_name || '') || '—', updated: String(emp.updated_by_name || '') || '—', at: Number(emp.updated_at) ? fmtDateTime(Number(emp.updated_at)) : '—' })}</div>}
              </>
            )}
            {(tab === 'documents' || docsPending) && body && (
              <div style={tab === 'documents' ? undefined : { display: 'none' }}><DocsTab ctx={ctx} body={body} onPending={setDocsPending} t={t} /></div>
            )}
            {tab === 'history' && body && <HistoryTab ctx={ctx} id={id} schema={schema} accounts={body.expenseAccount ? [...accounts, body.expenseAccount] : accounts} t={t} />}
            {tab === 'payroll' && body && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-payroll-tab>
                <RunsTab ctx={ctx} id={id} t={t} />
                <OpeningsCard ctx={ctx} id={id} today={body.today} t={t} />
              </div>
            )}
          </div>
          <Readiness issues={issues} answer={answer} onJump={jump} t={t} />
        </div>
      )}
      {separating && body && <SeparateDialog today={body.today} onClose={() => setSeparating(false)} onSubmit={separate} t={t} />}
    </Dialog>
  );
}

// ── Recording the last day (small: three fields) ─────────────────────────────────────────────────────
// It opens on the server's today (Bangkok), not the browser's. ⛔ It closes only when the write went
// through: a refusal is said here, with the date, kind and reason kept as typed.
function SeparateDialog({ today, onClose, onSubmit, t }: { today: string; onClose: () => void; onSubmit: (v: { date: string; kind: string; reason: string }) => Promise<string>; t: TF }) {
  const [date, setDate] = useState(today);
  const [kind, setKind] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  const ready = /^\d{4}-\d{2}-\d{2}$/.test(date) && !!kind && reason.length <= 300;
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try { const why = await onSubmit({ date, kind, reason: reason.trim() }); if (why) setErr(why); } finally { setBusy(false); }
  };
  return (
    <Dialog title={t('prl_separate_title')} onClose={onClose} busy={busy} dirty={dirty} width={460}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-user-minus" label={t('prl_separate_btn')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={note}>{t('prl_separate_note')}</div>
        {err && <div style={errBox} data-sep-error>{err}</div>}
        <div><label style={lbl}>{t('prl_f_termination_date')}</label><input type="date" value={date} onChange={e => ed(setDate, e.target.value)} style={field} data-sep="date" /></div>
        <div><label style={lbl}>{t('prl_f_separation_kind')}</label>
          <select value={kind} onChange={e => ed(setKind, e.target.value)} style={field} data-sep="kind">
            <option value="">{t('prl_choose')}</option>
            <option value="resigned">{t('prl_opt_separation_kind_resigned')}</option>
            <option value="terminated">{t('prl_opt_separation_kind_terminated')}</option>
          </select>
        </div>
        <div><label style={lbl}>{t('prl_f_separation_reason')}</label><input value={reason} onChange={e => ed(setReason, e.target.value)} style={field} data-sep="reason" /></div>
      </div>
    </Dialog>
  );
}

// ── The photo: a client_docs file, set at once (logged as a change of photo_doc_id) ──────────────────
function PhotoBox({ ctx, body, disabled, onDone, t }: { ctx: AwCtx; body: PrEmployeeBody; disabled: boolean; onDone: (b: PrEmployeeBody) => void; t: TF }) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [inputKey, setInputKey] = useState(0);
  const photo = String(body.details?.photo_doc_id || '');
  const empId = String(body.employee?.id || '');
  useEffect(() => {
    let alive = true; let made = '';
    setUrl('');
    if (photo) payrollApi.fileUrl(ctx.clientId, empId, photo).then(u => { made = u; if (alive) setUrl(u); else URL.revokeObjectURL(u); }).catch(() => { /* no picture shown */ });
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [ctx.clientId, empId, photo]);
  const upload = async (f: File | undefined) => {
    setInputKey(k => k + 1);   // the same file can be picked again
    if (!f || busy) return;
    // ⛔ A file the route refuses (a type it does not take, or over its size) is never sent: the accept list is
    // only a hint to the picker.
    const why = fileProblem(t, f, PR_PHOTO_TYPES);
    if (why) { setErr(why); return; }
    setBusy(true); setErr('');
    try { onDone(await payrollApi.setPhoto(ctx.clientId, empId, body.rowVersion, f)); } catch (e: any) { setErr(prErr(e, t)); } finally { setBusy(false); }
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }} data-photo>
      <div style={{ width: 56, height: 56, borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden', background: 'var(--bg-app)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {url ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <i className="ti ti-user" style={{ fontSize: 22, color: 'var(--text-tertiary)' }} aria-hidden />}
      </div>
      <label style={{ ...actBtn, opacity: disabled || busy ? 0.5 : 1 }}>
        <i className={busy ? 'ti ti-loader' : 'ti ti-camera'} aria-hidden /> {t(photo ? 'prl_photo_change' : 'prl_photo_add')}
        <input key={inputKey} type="file" accept={PR_PHOTO_TYPES.join(',')} disabled={disabled || busy} style={{ display: 'none' }} onChange={e => upload(e.target.files?.[0])} />
      </label>
      {disabled && <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('prl_save_first')}</span>}
      {err && <span style={{ fontSize: 11.5, color: '#B42318' }}>{err}</span>}
    </div>
  );
}

// ── Documents: the ID document first (read-only, as filed with the client), then the employee's own ──
// A file chosen or a description typed and not uploaded yet is reported up (`onPending`): the window's close
// asks before it is lost — through the Dialog's `dirty`, the one question — and nothing else waits on it.
// The window keeps this tab mounted while it is pending, so another tab never throws it away.
function DocsTab({ ctx, body, onPending, t }: { ctx: AwCtx; body: PrEmployeeBody; onPending: (pending: boolean) => void; t: TF }) {
  const empId = String(body.employee?.id || '');
  const [data, setData] = useState<{ idDoc: PrEmployeeBody['idDoc']; docs: PrDoc[] } | null>(null);
  const [label, setLabel] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let alive = true;
    payrollApi.docs(ctx.clientId, empId).then(d => { if (alive) setData(d); }).catch((e: any) => { if (alive) setErr(prErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, empId, reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Unmounted only with nothing pending, or with the window itself: either way nothing waits here any more.
  useEffect(() => { return () => onPending(false); }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const pend = (f: File | null, l: string) => onPending(!!f || l.trim() !== '');
  const choose = (f: File | null) => {
    // ⛔ A file the route refuses (a type it does not take, or over its size) is never chosen, so it can never be sent.
    const why = f ? fileProblem(t, f, PR_DOC_TYPES) : '';
    if (why) { setErr(why); setFile(null); setInputKey(k => k + 1); pend(null, label); return; }
    setErr(''); setFile(f); pend(f, label);
  };
  const open = async (clientDocId: string) => { try { openBlob(await payrollApi.fileUrl(ctx.clientId, empId, clientDocId)); } catch (e: any) { setErr(prErr(e, t)); } };
  const add = async () => {
    if (!file || busy) return;
    const why = fileProblem(t, file, PR_DOC_TYPES);
    if (why) { setErr(why); return; }
    setBusy(true); setErr('');
    try { await payrollApi.addDoc(ctx.clientId, empId, file, label.trim()); setFile(null); setLabel(''); setInputKey(k => k + 1); pend(null, ''); setReload(n => n + 1); }
    catch (e: any) { setErr(prErr(e, t)); } finally { setBusy(false); }
  };
  const del = async (d: PrDoc) => {
    if (busy || !window.confirm(t('prl_doc_remove_confirm', { name: d.label || d.filename }))) return;
    setBusy(true); setErr('');
    try { await payrollApi.removeDoc(ctx.clientId, empId, d.id); setReload(n => n + 1); } catch (e: any) { setErr(prErr(e, t)); } finally { setBusy(false); }
  };
  if (!data) return err ? <div style={errBox}>{err}</div> : <Loader />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-docs>
      {err && <div style={errBox}>{err}</div>}
      <div style={{ ...card, padding: 0 }}>
        <table style={table}>
          <thead><tr><th style={th}>{t('prl_doc_label')}</th><th style={th}>{t('prl_doc_file')}</th><th style={th}>{t('prl_doc_added')}</th><th style={th} aria-hidden /></tr></thead>
          <tbody>
            {data.idDoc && (
              <tr data-doc="id">
                <td style={td}>{t('prl_doc_id_card')} <span style={pillGrey}>{t('prl_doc_readonly')}</span></td>
                <td style={td}>{data.idDoc.filename}</td>
                <td style={td}>{data.idDoc.createdAt ? fmtDateTime(data.idDoc.createdAt) : '—'}</td>
                <td style={td}><button onClick={() => open(data.idDoc!.id)} style={actBtn}><i className="ti ti-eye" aria-hidden /> {t('prl_doc_open')}</button></td>
              </tr>
            )}
            {data.docs.map(d => (
              <tr key={d.id} data-doc={d.id}>
                <td style={td}>{d.label}</td>
                <td style={td}>{d.filename}</td>
                <td style={td}>{d.created_by_name} · {fmtDateTime(d.created_at)}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <button onClick={() => open(d.client_doc_id)} style={actBtn}><i className="ti ti-eye" aria-hidden /> {t('prl_doc_open')}</button>{' '}
                  <button onClick={() => del(d)} disabled={busy} style={{ ...actBtn, color: '#B42318' }} aria-label={t('prl_doc_remove')}><i className="ti ti-trash" aria-hidden /></button>
                </td>
              </tr>
            ))}
            {!data.idDoc && !data.docs.length && <tr><td colSpan={4} style={emptyLine}>{t('prl_doc_none')}</td></tr>}
          </tbody>
        </table>
      </div>
      <div style={{ ...card, display: 'flex', alignItems: 'end', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 200px' }}><label style={lbl}>{t('prl_doc_label')}</label><input value={label} onChange={e => { setLabel(e.target.value); pend(file, e.target.value); }} style={field} data-doc-label /></div>
        <div><label style={lbl}>{t('prl_doc_file')}</label><input key={inputKey} type="file" accept={PR_DOC_TYPES.join(',')} onChange={e => choose(e.target.files?.[0] || null)} data-doc-file /></div>
        <button onClick={add} disabled={!file || busy} style={{ ...btnPrimary, opacity: !file || busy ? 0.5 : 1 }} data-action="add-doc"><i className={busy ? 'ti ti-loader' : 'ti ti-upload'} aria-hidden /> {t('prl_doc_add')}</button>
      </div>
    </div>
  );
}

// ── History: every change to this employee, newest first (employee_change_log) ───────────────────────
function HistoryTab({ ctx, id, schema, accounts, t }: { ctx: AwCtx; id: string; schema: PrSchema; accounts: PrAccount[]; t: TF }) {
  const [data, setData] = useState<{ rows: PrHistoryRow[]; total: number; page: number; limit: number } | null>(null);
  const [page, setPage] = useState(1);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    payrollApi.history(ctx.clientId, id, page).then(d => { if (alive) setData(d); }).catch((e: any) => { if (alive) setErr(prErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, id, page]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <div style={errBox}>{err}</div>;
  if (!data) return <Loader />;
  const show = (f: string, v: any) => historyValue(t, schema, accounts, f, v);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-history>
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {!data.rows.length ? <div style={emptyLine}>{t('prl_history_none')}</div> : (
          <table style={table}>
            <thead><tr><th style={th}>{t('prl_hist_when')}</th><th style={th}>{t('prl_hist_change')}</th><th style={th}>{t('prl_hist_who')}</th><th style={th}>{t('prl_hist_through')}</th></tr></thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.id} data-hist-id={r.id} data-hist-field={r.field}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDateTime(r.at)}</td>
                  <td style={td}>
                    <b>{t(`prl_act_${r.action}`)}</b>{r.field && r.action === 'update' ? <> · {fieldLabel(t, r.field)}: <span style={{ color: 'var(--text-tertiary)' }} data-old>{show(r.field, r.old_value)}</span> → <span data-new>{show(r.field, r.new_value)}</span></> : null}
                  </td>
                  <td style={td}>{r.actor_name}</td>
                  <td style={{ ...td, fontSize: 11.5, color: 'var(--text-tertiary)' }} data-hist-source>{sourceLabel(t, r.source)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <Pagination page={page} total={data.total} limit={data.limit} onPage={setPage} />
    </div>
  );
}

// ── Payroll: the runs this person was on (batch 10 R6) ───────────────────────────────────────────────
// Their own line on each run, newest first, 100 a page: the run's number, period (day-first), type and state
// in the reader's language, and this person's gross, social security, withholding and net — the server's
// figures, '—' where the run has not worked one out. A void run is struck through; a line taken off a run is
// marked. A row opens its run in Payroll › Payroll List.
const PND1 = 'ภ.ง.ด.1';
const runPill = (s: string): React.CSSProperties =>
  s === 'approved' || s === 'posted' ? pillGreen : s === 'calculated' ? pillAmber : s === 'void' || s === 'reversed' ? pillRed : pillGrey;
const satMoney = (sat: number | null | undefined): string => (sat === null || sat === undefined ? '—' : money((Number(sat) || 0) / 100));
function RunsTab({ ctx, id, t }: { ctx: AwCtx; id: string; t: TF }) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PrEmpRunsPage | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    payrollApi.runs(ctx.clientId, id, page).then(d => { if (alive) { setData(d); setErr(''); } }).catch((e: any) => { if (alive) setErr(prErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, id, page]);   // eslint-disable-line react-hooks/exhaustive-deps
  // ⛔ The shell's door (ctx.setNav): it asks first when this window holds typing (guardLeave) and, in the same
  // write, clears this pane's ?emp and ?etab — the employee window closes as the run opens.
  const open = (runId: string) => ctx.setNav('payroll', 'payroll_list', { run: runId, emp: '', etab: '' });
  const right: React.CSSProperties = { ...th, textAlign: 'right' };
  return (
    <section style={{ ...card, padding: 0 }} data-runs data-total={data ? data.total : undefined}>
      <div style={{ ...sectionHead, padding: '12px 14px 4px' }}>{t('prl_runs_title')}</div>
      {err ? <div style={{ ...errBox, margin: 12 }}>{err}</div> : !data ? <Loader /> : !data.rows.length ? <div style={emptyLine} data-runs-none>{t('prl_runs_none')}</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>{t('prl_runs_col_doc')}</th><th style={th}>{t('prl_runs_col_period')}</th><th style={th}>{t('prl_runs_col_type')}</th>
                <th style={th}>{t('prl_runs_col_status')}</th><th style={right}>{t('prl_runs_col_gross')}</th><th style={right}>{t('prl_runs_col_sso')}</th>
                <th style={right}>{PND1}</th><th style={right}>{t('prl_runs_col_net')}</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(r => {
                const removed = !!Number(r.removed);
                const struck: React.CSSProperties = r.status === 'void' ? { textDecoration: 'line-through', color: 'var(--text-tertiary)' } : {};
                return (
                  <tr key={r.runId} data-run-row={r.runId} data-run-status={r.status} data-run-removed={removed ? '1' : undefined} tabIndex={0} role="link"
                    title={t('prl_runs_open_hint')} onClick={() => open(r.runId)} onKeyDown={e => { if (e.key === 'Enter') open(r.runId); }}
                    style={{ cursor: 'pointer', ...(removed ? { background: 'var(--bg-app)' } : {}) }}>
                    <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 700, color: 'var(--accent)', fontVariantNumeric: 'tabular-nums', ...struck }} data-run-doc>{r.docNo}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap', ...struck }} data-run-period>{dmy(r.periodFrom)} – {dmy(r.periodTo)}</td>
                    <td style={{ ...td, ...struck }}>{runTypeLabel(t, String(r.runType))}</td>
                    <td style={td}>
                      <span style={runPill(String(r.status))} data-run-status-word>{runStatusLabel(t, String(r.status))}</span>
                      {removed && <div style={{ fontSize: 11, color: '#92400E', marginTop: 3 }} data-run-removed-mark>{t('prl_runs_removed')}</div>}
                    </td>
                    <td style={{ ...num, ...struck }}>{satMoney(r.grossSat)}</td>
                    <td style={{ ...num, ...struck }}>{satMoney(r.ssoEeSat)}</td>
                    <td style={{ ...num, ...struck }}>{satMoney(r.whtSat)}</td>
                    <td style={{ ...num, ...struck }}>{satMoney(r.netSat)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {data && <div style={{ padding: '0 12px 10px' }}><Pagination page={page} total={data.total} limit={data.limit || 100} onPage={setPage} /></div>}
    </section>
  );
}

// ── The tax year to date, and its opening (batch 10 R6, design §2.5 employee_ytd_openings) ─────────────────
// Month by month, what a payroll run reads for this person to work out the ภ.ง.ด.1 withholding
// (services/payrollInputs.ts ytdFor, through GET /employees/:id/ytd-openings): the opening, the month's ภ.ง.ด.1
// line (or the figure captured when it was filed), a GAP (employed, nothing on record — Calculate refuses it), or
// nothing (not employed then). This year's months from the current Bangkok month on read "—": they have not
// happened. The opening (one per tax year) carries what this employer paid before payroll ran here, months 1 to
// its through-month; it is set in a small form and written by ONE PUT, logged as a payroll event under the
// logged-in user. ⛔ A read-only seat gets no Edit.
function OpeningsCard({ ctx, id, today, t }: { ctx: AwCtx; id: string; today: string; t: TF }) {
  const readOnly = isReadOnlySeat(ctx);
  const curYear = Number(String(today).slice(0, 4)) || new Date().getFullYear();
  const curMonth = Number(String(today).slice(5, 7)) || 1;
  const [year, setYear] = useState(curYear);
  const [data, setData] = useState<PrYtdBody | null>(null);
  const [err, setErr] = useState('');
  const [reload, setReload] = useState(0);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    let alive = true;
    setErr('');
    payrollApi.ytdOpenings(ctx.clientId, id, year).then(d => { if (alive) setData(d); }).catch((e: any) => { if (alive) { setData(null); setErr(prErr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, id, year, reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  const shown = data && Number(data.year) === year ? data : null;
  // The months that have happened: this year's before the current month, all twelve of an earlier year.
  const past = (m: number) => year < curYear || (year === curYear && m < curMonth);
  const monthOf = (m: number) => (shown?.months || []).find(x => Number(x.month) === m);
  const gaps = Array.from({ length: 12 }, (_, i) => i + 1).filter(m => past(m) && monthOf(m)?.source === 'gap');
  const op: PrYtdOpening | null = shown?.opening || null;
  const amount = (m: number, sat: number) => {
    const row = monthOf(m);
    return !past(m) || !row || row.source === 'opening' || row.source === 'gap' || row.source === 'none' ? '—' : money((Number(sat) || 0) / 100);
  };
  const right: React.CSSProperties = { ...th, textAlign: 'right' };
  return (
    <section style={{ ...card, display: 'flex', flexDirection: 'column', gap: 10 }} data-ytd-card data-year={year}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ ...sectionHead, marginBottom: 0 }}>{t('prl_ytd_title')}</div>
        <label htmlFor="prl-ytd-year" style={{ ...lbl, marginBottom: 0 }}>{t('prl_ytd_year')}</label>
        <select id="prl-ytd-year" value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...inp, flex: 'none' }} data-ytd-year>
          {[curYear, curYear - 1].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        {!readOnly && shown && (
          <button style={actBtn} onClick={() => setEditing(true)} data-ytd-edit><i className="ti ti-pencil" aria-hidden /> {t('prl_ytd_edit')}</button>
        )}
      </div>
      <div style={note}>{t('prl_ytd_lead', { form: PND1 })}</div>
      {err ? <div style={errBox}>{err}</div> : !shown ? <Loader /> : (
        <>
          <div style={{ fontSize: 12.5, display: 'flex', flexDirection: 'column', gap: 2 }} data-ytd-opening={op ? String(op.throughMonth) : 'none'}>
            <span>{!op ? t('prl_ytd_opening_none', { year })
              : Number(op.throughMonth) >= 1 ? t('prl_ytd_opening_line', { through: mmYyyy(year, Number(op.throughMonth)), income: satMoney(op.incomeSat), withheld: satMoney(op.withheldSat), sso: satMoney(op.ssoSat) })
                : t('prl_ytd_opening_cleared', { year })}</span>
            {op?.note && <span style={{ color: 'var(--text-secondary)' }} data-ytd-note>{op.note}</span>}
            {op?.setByName && <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }} data-ytd-set-by>{t('prl_ytd_set_by', { who: op.setByName, at: op.setAt ? fmtDateTime(op.setAt) : '—' })}</span>}
          </div>
          {!!gaps.length && (
            <div style={{ fontSize: 12, color: '#92400E', display: 'flex', gap: 6 }} data-ytd-gaps={gaps.join(',')}>
              <i className="ti ti-alert-triangle" aria-hidden style={{ marginTop: 2 }} />{t('prl_ytd_gaps', { months: gaps.map(m => mmYyyy(year, m)).join(', ') })}
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table style={table} data-ytd>
              <thead>
                <tr>
                  <th style={th}>{t('prl_ytd_col_month')}</th><th style={th}>{t('prl_ytd_col_source')}</th>
                  <th style={right}>{t('prl_ytd_col_income')}</th><th style={right}>{t('prl_ytd_col_withheld')}</th><th style={right}>{t('prl_ytd_col_sso')}</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => {
                  const row = monthOf(m);
                  const src = past(m) && row ? String(row.source) : '';
                  return (
                    <tr key={m} data-ytd-month={m} data-ytd-source={src || 'future'} style={src === 'gap' ? { background: '#FFFBEB' } : undefined}>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{mmYyyy(year, m)}</td>
                      <td style={{ ...td, ...(src === 'gap' ? { color: '#92400E', fontWeight: 700 } : src === 'none' ? { color: 'var(--text-tertiary)' } : {}) }} data-ytd-source-text>
                        {src ? ytdSourceLabel(t, src, mmYyyy(year, m), PND1) : '—'}
                      </td>
                      <td style={num}>{amount(m, row?.incomeSat ?? 0)}</td>
                      <td style={num}>{amount(m, row?.withheldSat ?? 0)}</td>
                      <td style={num}>{amount(m, row?.ssoSat ?? 0)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      {editing && shown && !readOnly && (
        <OpeningsDialog ctx={ctx} id={id} year={year} lastMonth={year < curYear ? 12 : Math.max(0, curMonth - 1)} opening={op}
          onClose={() => setEditing(false)} onSaved={() => { setEditing(false); setReload(n => n + 1); }} />
      )}
    </section>
  );
}

// A money box as the house types one (the Property Form's price box): digits and one point, at most two decimals,
// grouped in thousands as it is typed, the firm's currency code inside its right end. What is kept is the plain
// figure; '' is no answer (zero is typed, never assumed).
const moneyTyped = (v: string): string => {
  const s = String(v || '').replace(/[^\d.]/g, '');
  const i = s.indexOf('.');
  return i < 0 ? s : `${s.slice(0, i)}.${s.slice(i + 1).replace(/\./g, '').slice(0, 2)}`;
};
const grouped = (v: string): string => { if (!v) return ''; const [a, b] = v.split('.'); return a.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (b !== undefined ? `.${b}` : ''); };
/** A typed figure → satang; null when it is not one (a blank included). */
const satOfTyped = (v: string): number | null => (/^(\d+(\.\d{0,2})?|\.\d{1,2})$/.test(v) ? Math.round(Number(v) * 100) : null);
const typedOfSat = (sat: number): string => { const n = Math.max(0, Math.round(Number(sat) || 0)); return `${Math.floor(n / 100)}.${String(n % 100).padStart(2, '0')}`; };
function MoneyInput({ id, value, onChange, bad, data }: { id: string; value: string; onChange: (v: string) => void; bad: boolean; data: string }) {
  return (
    <div style={{ position: 'relative' }}>
      <input id={id} value={grouped(value)} onChange={e => onChange(moneyTyped(e.target.value))} inputMode="decimal" data-ytd-f={data}
        style={{ ...field, paddingRight: 48, fontVariantNumeric: 'tabular-nums', ...(bad ? { border: '1px solid #F3B9B9', background: '#FFF8F8' } : {}) }} />
      <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)' }}>{currencyCode()}</span>
    </div>
  );
}
/** The server's refusal of an opening, named by the field it gives (400 invalid {field}). */
const YTD_FIELD_KEYS: Record<string, string> = {
  throughMonth: 'prl_ytd_f_through', incomeSat: 'prl_ytd_f_income', withheldSat: 'prl_ytd_f_withheld', ssoSat: 'prl_ytd_f_sso', note: 'prl_ytd_f_note',
};
const ytdErr = (e: any, t: TF): string => {
  const p = e?.payload || {};
  const k = YTD_FIELD_KEYS[String(p.field || '')];
  return p.code === 'invalid' && k ? t('prl_ytd_err_invalid', { field: t(k) }) : prErr(e, t);
};

// ── The opening for one tax year (small: a month, three amounts, a note) ───────────────────────────────────
// Nothing is prefilled for a year without one — every amount is typed, 0 included, because "nothing was paid
// before" is an answer the run relies on. ⛔ Save stays off until the server would take it (a month 0–12 and three
// figures of at most two decimals, none below zero), so a refused PUT is never sent; it closes only when the
// write went through, and a refusal is said here with the typing kept.
function OpeningsDialog({ ctx, id, year, lastMonth, opening, onClose, onSaved }: {
  ctx: AwCtx; id: string; year: number; lastMonth: number; opening: PrYtdOpening | null; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [through, setThrough] = useState(opening ? String(opening.throughMonth) : '');
  const [income, setIncome] = useState(opening ? typedOfSat(opening.incomeSat) : '');
  const [withheld, setWithheld] = useState(opening ? typedOfSat(opening.withheldSat) : '');
  const [sso, setSso] = useState(opening ? typedOfSat(opening.ssoSat) : '');
  const [noteText, setNoteText] = useState(opening?.note || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  const months = Array.from(new Set([...Array.from({ length: lastMonth + 1 }, (_, i) => i), ...(opening ? [Number(opening.throughMonth)] : [])])).sort((a, b) => a - b);
  const sats = [income, withheld, sso].map(satOfTyped);
  const ready = through !== '' && sats.every(x => x !== null) && noteText.length <= 300;
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try {
      await payrollApi.setYtdOpening(ctx.clientId, id, year, {
        throughMonth: Number(through), incomeSat: sats[0] as number, withheldSat: sats[1] as number, ssoSat: sats[2] as number, note: noteText.trim() });
      setDirty(false); onSaved();
    } catch (e: any) { setErr(ytdErr(e, t)); setBusy(false); }
  };
  const bad = (v: string) => v !== '' && satOfTyped(v) === null;
  return (
    <Dialog width={480} title={t('prl_ytd_dlg_title', { year })} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-device-floppy" label={t('prl_ytd_save')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-ytd-form>
        <div style={note}>{t('prl_ytd_dlg_note')}</div>
        {err && <div style={errBox} data-ytd-error>{err}</div>}
        <div>
          <label htmlFor="prl-ytd-through" style={lbl}>{t('prl_ytd_f_through')}</label>
          <select id="prl-ytd-through" value={through} onChange={e => ed(setThrough, e.target.value)} style={field} data-ytd-f="through">
            <option value="">{t('prl_choose')}</option>
            {months.map(m => <option key={m} value={String(m)}>{m === 0 ? t('prl_ytd_through_none') : mmYyyy(year, m)}</option>)}
          </select>
        </div>
        {([['income', income, setIncome, 'prl_ytd_f_income'], ['withheld', withheld, setWithheld, 'prl_ytd_f_withheld'], ['sso', sso, setSso, 'prl_ytd_f_sso']] as [string, string, (v: string) => void, string][]).map(([k, v, set, labelKey]) => (
          <div key={k}>
            <label htmlFor={`prl-ytd-${k}`} style={lbl}>{t(labelKey)}</label>
            <MoneyInput id={`prl-ytd-${k}`} value={v} onChange={x => ed(set, x)} bad={bad(v)} data={k} />
            {bad(v) && <div style={{ fontSize: 11, color: '#B42318', marginTop: 3 }} data-ytd-bad={k}>{t('prl_ytd_bad_amount')}</div>}
          </div>
        ))}
        <div>
          <label htmlFor="prl-ytd-note" style={lbl}>{t('prl_ytd_f_note')}</label>
          <input id="prl-ytd-note" value={noteText} maxLength={300} onChange={e => ed(setNoteText, e.target.value)} style={field} data-ytd-f="note" />
        </div>
      </div>
    </Dialog>
  );
}
